/**
 * Workshop persistence via api-gateway ``/v1/workshop/pages``.
 *
 * Pages are team-scoped server-side (session cookie); switching device or
 * browser keeps data as long as the same team session is used.
 */

import { ApiError, api } from "@/lib/api";

import { INTRO_TEMPLATE } from "./templates";
import type { Widget, WidgetSlot, WorkshopPage } from "./types";

export interface WorkshopPageWire {
  id: string;
  title: string;
  description?: string | null;
  kb_id: string | null;
  kb_name: string | null;
  source_prompt?: string | null;
  last_analyze_source?: string | null;
  widgets: WidgetSlot[];
  standalone_html?: string | null;
  created_at: string;
  updated_at: string;
}

function wireToPage(row: WorkshopPageWire): WorkshopPage {
  return {
    id: row.id,
    title: row.title,
    description: row.description ?? undefined,
    kb_id: row.kb_id,
    kb_name: row.kb_name,
    widgets: row.widgets,
    standalone_html: row.standalone_html ?? undefined,
    source_prompt: row.source_prompt ?? undefined,
    last_analyze_source: row.last_analyze_source ?? undefined,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

export async function fetchWorkshopPages(): Promise<WorkshopPage[]> {
  const res = await api<{ items: WorkshopPageWire[] }>("/workshop/pages");
  return res.items.map(wireToPage);
}

export async function createWorkshopPageRemote(
  draft: Omit<WorkshopPage, "id" | "created_at" | "updated_at">,
): Promise<WorkshopPage> {
  const body = {
    title: draft.title,
    description: draft.description ?? null,
    kb_id: draft.kb_id,
    kb_name: draft.kb_name,
    source_prompt: draft.source_prompt ?? null,
    last_analyze_source: draft.last_analyze_source ?? null,
    widgets: draft.widgets.map((s) => ({
      id: s.id,
      span: s.span,
      widget: s.widget,
    })),
    standalone_html: draft.standalone_html ?? null,
  };
  const row = await api<WorkshopPageWire>("/workshop/pages", {
    method: "POST",
    body: JSON.stringify(body),
  });
  return wireToPage(row);
}

export async function updateWorkshopPageRemote(
  id: string,
  patch: Partial<
    Pick<
      WorkshopPage,
      | "title"
      | "description"
      | "widgets"
      | "standalone_html"
      | "kb_id"
      | "kb_name"
      | "source_prompt"
      | "last_analyze_source"
    >
  >,
): Promise<WorkshopPage> {
  const body: Record<string, unknown> = {};
  if (patch.title !== undefined) body.title = patch.title;
  if (patch.description !== undefined) body.description = patch.description ?? null;
  if (patch.kb_id !== undefined) body.kb_id = patch.kb_id;
  if (patch.kb_name !== undefined) body.kb_name = patch.kb_name;
  if (patch.source_prompt !== undefined) body.source_prompt = patch.source_prompt ?? null;
  if (patch.last_analyze_source !== undefined) {
    body.last_analyze_source = patch.last_analyze_source ?? null;
  }
  if (patch.widgets !== undefined) {
    body.widgets = patch.widgets.map((s) => ({
      id: s.id,
      span: s.span,
      widget: s.widget,
    }));
  }
  if (patch.standalone_html !== undefined) {
    body.standalone_html = patch.standalone_html ?? null;
  }
  const row = await api<WorkshopPageWire>(`/workshop/pages/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
  return wireToPage(row);
}

export async function deleteWorkshopPageRemote(id: string): Promise<void> {
  await api<void>(`/workshop/pages/${id}`, { method: "DELETE" });
}

export async function reanalyzeWorkshopPageRemote(id: string): Promise<WorkshopPage> {
  const row = await api<WorkshopPageWire>(`/workshop/pages/${id}/reanalyze`, {
    method: "POST",
  });
  return wireToPage(row);
}

/** Seed intro when API returns no rows (first visit for this team). */
export async function seedIntroPageRemote(kb: {
  id: string | null;
  name: string | null;
}): Promise<WorkshopPage> {
  const built = INTRO_TEMPLATE.build(kb);
  return createWorkshopPageRemote({
    ...built,
    source_prompt: undefined,
    last_analyze_source: undefined,
    standalone_html: undefined,
  });
}

export function workshopLoadErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 404 && err.message.includes("workshop")) return err.message;
    if (err.status >= 500) {
      return `服务器错误：${err.message}（如首次启用 Workshop，请运行 alembic upgrade 0022_workshop_pages）`;
    }
    return err.message;
  }
  return "加载 Workshop 失败，请确认已登录且数据库已迁移";
}

export type { Widget, WidgetSlot, WorkshopPage };
